import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Handshake, Gem, ShieldCheck, MapPin, ArrowRight } from "lucide-react";
import { IMAGES } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="mask-line">
        <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-70px" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
        className={className}
    >
        {children}
    </motion.div>
);

const MILESTONES = [
    ["25+", "Years of collective industry experience"],
    ["Kanpur", "On the banks of the River Ganges"],
    ["End-to-End", "Raw leather to finished product"],
    ["Global", "Americas · Europe · Australia"],
];

const RANGE = [
    "Laptop & Business Bags",
    "Sling & Crossbody Bags",
    "Wallets",
    "Belts",
    "Leather Accessories",
    "Men & Women Lines",
];

const SUPPORT = ["Material selection", "Product specifications", "Branding & embossing", "Finishing & packing"];

const VALUES = [
    { icon: Handshake, name: "Trust", text: "Lasting business relationships built on honest dealing, quote to shipment." },
    { icon: ShieldCheck, name: "Consistency", text: "Reliable products and dependable service, order after order." },
    { icon: Gem, name: "Transparency", text: "Collaborative development with clear communication at every stage." },
];

export default function About() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div data-testid="about-page" className="bg-[#0F0C08]">
            {/* Header */}
            <section className="relative pt-40 pb-16 sm:pb-24 overflow-hidden grain">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(55% 45% at 75% 12%, rgba(197,139,73,0.16) 0%, rgba(197,139,73,0) 70%)" }}
                />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-6"
                        data-testid="about-eyebrow"
                    >
                        About FnB International
                    </motion.p>
                    <h1 className="font-display font-semibold text-[#F4EFEA] text-[10.5vw] sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight" data-testid="about-headline">
                        <MaskedLine delay={0.25}>A legacy of craftsmanship.</MaskedLine>
                        <MaskedLine delay={0.4}>
                            A vision for the <em className="italic font-medium text-[#D4A373]">future</em>.
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                        className="mt-8 max-w-2xl text-base sm:text-lg text-[#C2B7AD] leading-relaxed"
                        data-testid="about-intro"
                    >
                        FnB International is a manufacturing and export enterprise built on a legacy
                        of leather craftsmanship, industry experience and a commitment to quality.
                        Founded by Mohd Farrukh, the company brings together over 25 years of
                        collective industry experience — expertise spanning the leather value chain,
                        from raw leather processing to finished leather products.
                    </motion.p>
                </div>
            </section>

            {/* Milestone strip */}
            <section className="border-y border-[#2E2620] bg-[#14110F]">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="about-milestones">
                    {MILESTONES.map(([v, l], i) => (
                        <Reveal key={l} delay={i * 0.08}>
                            <div className="font-display text-3xl sm:text-4xl text-[#D4A373]">{v}</div>
                            <div className="mt-1.5 text-xs tracking-[0.14em] uppercase text-[#8C8075] leading-relaxed">{l}</div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Rooted in Kanpur */}
            <section className="py-20 sm:py-28 bg-[#181410] grain relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <div className="overflow-hidden rounded-md border border-[#2E2620] group">
                            <img
                                src={IMAGES.artisan}
                                alt="FnB International artisan at work"
                                loading="lazy"
                                className="w-full h-[380px] sm:h-[460px] object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                                data-testid="about-artisan-image"
                            />
                        </div>
                    </Reveal>
                    <div>
                        <Reveal>
                            <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4 flex items-center gap-2">
                                <MapPin size={13} strokeWidth={1.8} /> Kanpur, Uttar Pradesh, India
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.1]">
                                Rooted in a city renowned for <em className="italic text-[#D4A373]">leather</em>.
                            </h2>
                            <p className="mt-6 text-base text-[#C2B7AD] leading-relaxed">
                                Our manufacturing roots lie near the banks of the River Ganges, in a
                                city whose name is synonymous with the leather industry. Our deep
                                understanding of leather, materials, manufacturing processes and
                                evolving market requirements enables us to develop products that
                                balance functionality, durability and refined design.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <blockquote className="mt-8 pl-5 border-l-2 border-[#C58B49]" data-testid="about-founder-quote">
                                <p className="font-display italic text-xl sm:text-2xl text-[#E2D4C9] leading-snug">
                                    "We connect people and businesses across borders — through trade
                                    that builds understanding."
                                </p>
                                <footer className="mt-3 text-xs tracking-[0.2em] uppercase text-[#8C8075]">
                                    Mohd Farrukh — Founder
                                </footer>
                            </blockquote>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Leather expertise */}
            <section className="py-20 sm:py-28">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <Reveal>
                            <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                                Our Expertise in Leather Manufacturing
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.1]">
                                Traditional craft, modern <em className="italic text-[#D4A373]">technique</em>.
                            </h2>
                            <p className="mt-6 text-base text-[#C2B7AD] leading-relaxed">
                                We combine traditional craftsmanship with modern manufacturing
                                techniques to create leather products for contemporary lifestyles and
                                global markets. Every buyer has unique requirements — so we focus on
                                flexible manufacturing, made-to-order products and OEM / private-label
                                development.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="about-range-chips">
                                {RANGE.map((r) => (
                                    <span key={r} className="px-4 py-2 rounded-sm border border-[#2E2620] bg-[#1D1814] text-[12px] tracking-[0.1em] uppercase text-[#C2B7AD]">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-8 text-[11px] tracking-[0.25em] uppercase text-[#8C8075] mb-3">We support you at every stage</p>
                            <ul className="grid sm:grid-cols-2 gap-3">
                                {SUPPORT.map((s) => (
                                    <li key={s} className="flex items-center gap-3 text-sm text-[#C2B7AD]">
                                        <span className="w-1.5 h-1.5 rotate-45 bg-[#C58B49] shrink-0" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="overflow-hidden rounded-md border border-[#2E2620] group">
                            <img
                                src={IMAGES.tools}
                                alt="Leathercraft tools in the FnB International workshop"
                                loading="lazy"
                                className="w-full h-[380px] sm:h-[480px] object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                                data-testid="about-tools-image"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Expanding horizons */}
            <section className="py-20 sm:py-28 bg-[#181410] grain relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-3xl mb-12">
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">Expanding Our Horizons</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.1]">
                            Beyond leather, with the same <em className="italic text-[#D4A373]">care</em>.
                        </h2>
                        <p className="mt-6 text-base text-[#C2B7AD] leading-relaxed">
                            While leather remains a core part of our heritage, we continue to expand
                            into complementary categories that combine creativity, craftsmanship and
                            commercial potential — a forward-looking approach that serves a wider
                            range of customers and markets.
                        </p>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-5">
                        {[
                            { img: "/assets/candle-1.png", title: "Handcrafted Scented Candles", text: "Distinctive gifting and lifestyle products, hand-poured for retailers and corporate buyers.", testid: "about-expansion-candles" },
                            { img: "/assets/magnet-1.png", title: "Handmade Fridge Magnets", text: "Decorative souvenir magnets with custom artwork for distributors and international markets.", testid: "about-expansion-magnets" },
                        ].map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.1}>
                                <div data-testid={c.testid} className="group relative overflow-hidden rounded-md border border-[#2E2620] hover:border-[#C58B49]/50 transition-colors duration-500 min-h-[300px]">
                                    <img src={c.img} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.05]" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C08]/95 via-[#0F0C08]/40 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-6">
                                        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#F4EFEA]">{c.title}</h3>
                                        <p className="mt-2 text-sm text-[#C2B7AD] leading-relaxed max-w-md">{c.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Commitment */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="text-center mb-12">
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                            Our Commitment to Quality &amp; Customer Satisfaction
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F4EFEA] tracking-tight">
                            Partnerships built to <em className="italic text-[#D4A373]">last</em>.
                        </h2>
                        <p className="mt-5 max-w-2xl mx-auto text-sm sm:text-base text-[#8C8075] leading-relaxed">
                            Through continuous improvement, attention to detail and professional
                            standards, we deliver reliable products — while adopting responsible
                            manufacturing practices that support environmental awareness and
                            sustainable growth.
                        </p>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.name} delay={i * 0.1}>
                                <div
                                    data-testid={`about-value-${i}`}
                                    className="rounded-md border border-[#2E2620] bg-[#1D1814] p-8 hover:border-[#C58B49]/50 hover:-translate-y-1.5 transition-all duration-500"
                                >
                                    <div className="w-11 h-11 rounded-sm border border-[#2E2620] bg-[#14110F] flex items-center justify-center text-[#C58B49]">
                                        <v.icon size={18} strokeWidth={1.6} />
                                    </div>
                                    <h3 className="mt-5 font-display text-2xl font-semibold text-[#F4EFEA]">{v.name}</h3>
                                    <p className="mt-2 text-sm text-[#8C8075] leading-relaxed">{v.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Vision + CTA */}
            <section className="py-24 sm:py-32 bg-[#181410] grain relative overflow-hidden text-center">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-6">Our Vision</p>
                        <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#F4EFEA] leading-[1.2] tracking-tight" data-testid="about-closing">
                            Rooted in experience. Driven by craftsmanship.
                            <br />
                            <em className="italic text-[#D4A373]">Connected to global markets.</em>
                        </p>
                        <p className="mt-7 max-w-2xl mx-auto text-sm sm:text-base text-[#8C8075] leading-relaxed">
                            To establish FnB International as a trusted Indian manufacturing and
                            export partner — recognized for leather expertise, a diverse portfolio,
                            a customer-focused approach and an unwavering commitment to quality.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <button
                            data-testid="about-cta-btn"
                            onClick={() => navigate("/#enquiry")}
                            className="group mt-12 inline-flex items-center gap-2 px-9 py-4 bg-[#C58B49] text-[#0F0C08] text-[13px] font-medium tracking-[0.16em] uppercase rounded-sm hover:bg-[#D4A373] transition-colors duration-300"
                        >
                            Start the Conversation
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
